'use client'

import { useMemo, useState } from 'react'
import { usePathname } from 'next/navigation'
import { motion } from 'framer-motion'
import { ChevronRight, MapPinHouse, Search } from 'lucide-react'
import { navigateUrl } from '@/utils/client-request'
import BranchCard from './BranchCard'

const ALL = 'Бүгд'

export default function BranchList({ branches, vendor }: { branches: any, vendor?: any }) {
  const pathname = usePathname()
  const vendorName = vendor?.name || 'Дэлгүүр'
  const [query, setQuery] = useState('')
  const [city, setCity] = useState<string>(ALL)

  const activeBranches = useMemo(() => (branches || []).filter((b: any) => b?.isActive !== false), [branches])

  const cities = useMemo(() => {
    const map = new Map<string, number>()
    map.set(ALL, activeBranches.length)
    activeBranches.forEach((b: any) => {
      const c = String(b?.city || '').trim()
      if (!c) return
      map.set(c, (map.get(c) || 0) + 1)
    })
    return [...map.entries()].map(([name, count]) => ({ name, count }))
  }, [activeBranches])

  const filtered = useMemo(() => {
    let list = activeBranches
    if (city !== ALL) {
      list = list.filter((b: any) => String(b?.city || '') === city)
    }
    if (query.trim()) {
      const q = query.trim().toLowerCase()
      list = list.filter((b: any) =>
        [b?.name, b?.address, b?.phone].filter(Boolean).some((v: string) => String(v).toLowerCase().includes(q))
      )
    }
    return list
  }, [activeBranches, query, city])

  return (
    <div>
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-10">
        <div className="hidden md:flex items-center gap-1.5 text-[12px] mb-8" style={{ color: 'rgba(255,255,255,0.55)' }}>
          <a href={navigateUrl('/', pathname)} className="hover:text-white transition-colors">Нүүр</a>
          <ChevronRight className="h-3 w-3" style={{ opacity: 0.5 }} />
          <span className="text-white font-medium">Салбарууд</span>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: 'easeOut' }}>
          <div className="mt-3 flex items-end justify-between gap-8 flex-wrap">
            <h1
              className="font-semibold tracking-[-0.025em] leading-[0.95] text-white max-w-3xl"
              style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 'clamp(36px, 7vw, 64px)' }}
            >
              Танд хамгийн ойрхон<br />салбар
            </h1>
            <div className="hidden md:block text-right pb-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Нийт</div>
              <div
                className="mt-1 font-semibold tabular-nums text-white leading-none"
                style={{ fontFamily: 'Gilroy, Inter, sans-serif', fontSize: 36 }}
              >
                {activeBranches.length}
              </div>
              <div className="text-[11px] mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>идэвхтэй салбар</div>
            </div>
          </div>
          <p className="mt-5 text-[15px] leading-7 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
            {vendorName}-ийн салбарууд — бараагаа очиж үзэх, эсхүл шууд дэлгүүрээс худалдан авах боломжтой.
          </p>
        </motion.div>
      </div>

      {(cities.length > 2 || activeBranches.length > 3) && (
        <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-10">
          <div className="flex items-center gap-2 flex-wrap">
            {cities.length > 2 && (
              <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide">
                {cities.map(c => {
                  const active = c.name === city
                  return (
                    <button
                      key={c.name}
                      onClick={() => setCity(c.name)}
                      className="h-9 px-4 inline-flex items-center gap-2 rounded-full text-[12.5px] font-medium transition-colors shrink-0"
                      style={{
                        background: active ? 'white' : 'rgba(255,255,255,0.035)',
                        color: active ? '#0a0a0c' : 'rgba(255,255,255,0.85)',
                        border: active ? '1px solid white' : '1px solid rgba(255,255,255,0.07)',
                      }}
                    >
                      {c.name}
                      <span
                        className="text-[10px] font-mono tabular-nums"
                        style={{ color: active ? 'rgba(10,10,12,0.6)' : 'rgba(255,255,255,0.4)' }}
                      >
                        {c.count}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
            {activeBranches.length > 3 && (
              <label className="relative inline-block w-full sm:w-64 sm:ml-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5" style={{ color: 'rgba(255,255,255,0.5)' }} />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Салбар хайх…"
                  className="w-full h-9 pl-9 pr-3 rounded-full text-[12.5px] outline-none border-0 focus:outline-none focus:ring-0"
                  style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)', color: 'white' }}
                />
              </label>
            )}
          </div>
        </div>
      )}

      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pb-20">
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((b: any, i: number) => (
              <BranchCard key={b.id} branch={b} vendor={vendor} index={i} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-24" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <MapPinHouse className="h-8 w-8" />
            <div className="text-[14px]">
              {query
                ? `"${query}" гэсэн нэртэй салбар олдсонгүй`
                : city !== ALL
                  ? `${city} хотод салбар байхгүй байна`
                  : 'Салбарын мэдээлэл хоосон байна'}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
